import React, { useEffect, useRef, useState } from 'react';
import { FileArchive, Upload, CheckCircle2, AlertTriangle, XCircle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { actaZipService } from '@/lib/api';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '');

const JobSummary = ({ job }) => (
  <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={`zip-job-summary-${job.id}`}>
    {job.status === 'processing' && <Badge className="bg-sky-100 text-sky-800"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Procesando {job.processed}/{job.total}</Badge>}
    {job.status === 'completed' && <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 className="w-3 h-3 mr-1" />Completado</Badge>}
    {job.status === 'failed' && <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Falló</Badge>}
    <span className="text-slate-600"><b>{job.matched}</b> vinculados</span>
    <span className="text-amber-700"><b>{job.unmatched}</b> sin acta</span>
    {job.failed > 0 && <span className="text-red-600"><b>{job.failed}</b> con error</span>}
    <span className="text-slate-400">· {job.total} archivos</span>
  </div>
);

const JobDetails = ({ job }) => (
  <div className="mt-2 space-y-2 text-xs" data-testid={`zip-job-details-${job.id}`}>
    {job.unmatched_files?.length > 0 && (
      <div className="rounded-md bg-amber-50 border border-amber-100 p-2">
        <p className="font-medium text-amber-800 mb-1"><AlertTriangle className="w-3.5 h-3.5 inline mr-1" />Sin acta coincidente ({job.unmatched_files.length}) — el nombre del archivo debe ser el N° de acta</p>
        <p className="text-amber-700 break-words">{job.unmatched_files.slice(0, 50).join(', ')}{job.unmatched_files.length > 50 ? ` … (+${job.unmatched_files.length - 50})` : ''}</p>
      </div>
    )}
    {job.errors?.length > 0 && (
      <div className="rounded-md bg-red-50 border border-red-100 p-2">
        <p className="font-medium text-red-800 mb-1">Errores ({job.errors.length})</p>
        {job.errors.slice(0, 20).map((e, i) => <p key={i} className="text-red-700">{e.file}: {e.error}</p>)}
      </div>
    )}
    {job.error && <p className="text-red-700">{job.error}</p>}
  </div>
);

const ActaZipUploader = ({ portfolios = [] }) => {
  const [file, setFile] = useState(null);
  const [portfolioId, setPortfolioId] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [open, setOpen] = useState(true);
  const inputRef = useRef(null);

  const loadJobs = async () => {
    try { setJobs((await actaZipService.jobs()).data || []); } catch { /* ignore */ }
  };

  useEffect(() => { loadJobs(); }, []);

  const processing = jobs.some((j) => j.status === 'processing');
  useEffect(() => {
    if (!processing) return;
    const t = setInterval(loadJobs, 2000);
    return () => clearInterval(t);
  }, [processing]);

  const handleUpload = async () => {
    if (!file) { toast.error('Seleccioná un archivo .zip'); return; }
    setUploading(true);
    try {
      const res = await actaZipService.upload(file, portfolioId !== 'all' ? portfolioId : null);
      toast.success(`ZIP recibido: ${res.data.total} archivos. Procesando…`);
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      setExpanded(res.data.id);
      loadJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir el ZIP');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="bg-white border-slate-200 p-4 mb-6" data-testid="acta-zip-uploader">
      <button type="button" className="w-full flex items-center justify-between text-left" onClick={() => setOpen(!open)} data-testid="acta-zip-toggle">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-rose-50 flex items-center justify-center"><FileArchive className="w-5 h-5 text-rose-600" /></div>
          <div>
            <p className="font-semibold text-slate-900 text-sm">Carga masiva de PDFs / videos de actas (ZIP)</p>
            <p className="text-xs text-slate-500">Cada archivo se vincula al acta cuyo número coincide con el nombre del archivo (ej. <code className="bg-slate-100 px-1 rounded">T-200933.pdf</code>)</p>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="mt-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <Select value={portfolioId} onValueChange={setPortfolioId}>
              <SelectTrigger className="sm:w-64" data-testid="acta-zip-portfolio"><SelectValue placeholder="Cartera" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Buscar en todas las carteras</SelectItem>
                {portfolios.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <input ref={inputRef} type="file" accept=".zip,application/zip" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-slate-200" data-testid="acta-zip-file-input" />
            <Button onClick={handleUpload} disabled={!file || uploading} className="bg-slate-900 hover:bg-slate-800" data-testid="acta-zip-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}Subir ZIP
            </Button>
          </div>
          <p className="text-xs text-slate-500">Hasta 100 MB por ZIP y 10 MB por archivo. Formatos: .pdf, .mp4, .webm, .mov. Si el acta ya tenía un archivo del mismo tipo, se reemplaza.</p>

          {jobs.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Últimas cargas</p>
              {jobs.map((job) => (
                <div key={job.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2" data-testid={`zip-job-${job.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{job.filename}</p>
                      <p className="text-[11px] text-slate-400">{fmtDate(job.created_at)}{job.created_by ? ` · ${job.created_by}` : ''}</p>
                    </div>
                    {(job.unmatched > 0 || job.failed > 0 || job.error) && (
                      <button type="button" className="text-xs text-slate-600 hover:text-slate-900 underline" onClick={() => setExpanded(expanded === job.id ? null : job.id)} data-testid={`zip-job-toggle-${job.id}`}>
                        {expanded === job.id ? 'Ocultar detalle' : 'Ver detalle'}
                      </button>
                    )}
                  </div>
                  <div className="mt-1"><JobSummary job={job} /></div>
                  {expanded === job.id && <JobDetails job={job} />}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default ActaZipUploader;
